const DARK_LUMINANCE_THRESHOLD = 0.55;

/**
 * Computes the WCAG relative luminance of a CSS color string.
 * Uses an off-screen canvas so any valid CSS color is supported.
 * Returns null when the color cannot be resolved.
 *
 * @param {string|undefined} color
 * @returns {number|null}
 */
function getRelativeLuminance(color) {
  const trimmed = color?.trim();
  if (!trimmed) return null;

  let r, g, b;

  if (typeof document !== "undefined") {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 1;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = trimmed;
      ctx.fillRect(0, 0, 1, 1);
      [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    } catch {
      return null;
    }
  } else {
    if (!trimmed.startsWith("#")) return null;
    const clean = trimmed.slice(1);
    if (clean.length === 3 || clean.length === 4) {
      r = parseInt(clean[0] + clean[0], 16);
      g = parseInt(clean[1] + clean[1], 16);
      b = parseInt(clean[2] + clean[2], 16);
    } else if (clean.length === 6 || clean.length === 8) {
      r = parseInt(clean.slice(0, 2), 16);
      g = parseInt(clean.slice(2, 4), 16);
      b = parseInt(clean.slice(4, 6), 16);
    } else {
      return null;
    }
  }

  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns the WCAG contrast ratio between two CSS color strings.
 * A ratio of 1 means identical; 21 is maximum (black on white).
 * Returns 1 when either color cannot be resolved.
 *
 * @param {string|undefined} c1
 * @param {string|undefined} c2
 * @returns {number}
 */
export function getContrastRatio(c1, c2) {
  const l1 = getRelativeLuminance(c1);
  const l2 = getRelativeLuminance(c2);
  if (l1 == null || l2 == null) return 1;
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Returns true when the given CSS color string resolves to a dark color/intensity
 * By using an off-screen canvas any valid CSS color is supported
 *
 * @param {string|undefined} color
 * @returns {boolean}
 */
export function isColorDark(color) {
  const trimmed = color?.trim();
  if (!trimmed) return false;

  if (typeof document !== "undefined") {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = 1;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = trimmed;
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
      const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      return luminance < DARK_LUMINANCE_THRESHOLD;
    } catch {
      return false;
    }
  }

  // Non-browser fallback
  const hex = trimmed;
  if (!hex.startsWith("#")) return false;
  const clean = hex.slice(1);
  let r, g, b;
  if (clean.length === 3 || clean.length === 4) {
    r = parseInt(clean[0] + clean[0], 16);
    g = parseInt(clean[1] + clean[1], 16);
    b = parseInt(clean[2] + clean[2], 16);
  } else if (clean.length === 6 || clean.length === 8) {
    r = parseInt(clean.slice(0, 2), 16);
    g = parseInt(clean.slice(2, 4), 16);
    b = parseInt(clean.slice(4, 6), 16);
  } else {
    return false;
  }

  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < DARK_LUMINANCE_THRESHOLD;
};

/**
 * Validates any CSS color or background string using the browser's style parser
 * Returns the trimmed value when valid or null
 *
 * @param {string|undefined} raw
 * @returns {string|null}
 */
export function resolveColorValue(raw) {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return null;

  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.style.background = "";
    el.style.background = trimmed;
    return el.style.background !== "" ? trimmed : null;
  }

  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)
    ? trimmed
    : null;
};

/**
 * Returns the resolved color from a string or a provided default value
 * Falls back to an empty string
 *
 * @param {string|undefined} raw
 * @param {string} defaultColor
 * @returns {string}
 */
export function resolveBackgroundColorValue(raw, defaultColor) {
  return resolveColorValue(raw) || defaultColor || "";
};

/**
 * Text color for use on a light background
 * If the given color is too light to read, returns dark gray to maintain visibility
 *
 * @param {string} color - Hex color (e.g. #abc or #aabbcc)
 * @returns {string} - Same received color or "#374151" when received color is too light
 */
export function resolveTextColorForLightBackgroundColor(color) {
  if (!color) return "#374151";
  const hex = String(color).replace(/^#/, "");
  const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.slice(4, 6), 16) / 255;
  const [rs, gs, bs] = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  const luminance = 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  return luminance > 0.7 ? "#374151" : color;
};

/**
 * Resolves the appropriate text color for use on a solid background
 * Returns dark gray for light backgrounds, white for dark backgrounds
 *
 * @param {string} color - Hex color (e.g. #abc or #aabbcc)
 * @returns {string} - "#374151" for light bg, "#fff" for dark bg
 */
export function resolveTextColorForBackgroundColor(color) {
  return isColorDark(color) ? "#fff" : "#374151";
};